import BaseViewController from "./base-view-controller";
import buildFieldByTemplate, {
  clearPreBuiltFields
} from "./helpers/template-fields";
import { manualPharmacyFormState } from "./helpers/pharmacy";
import {
  ACTION_TYPES,
  ACTION_VALUES,
  FIELD_NAMES,
  FIELD_POSITION
} from "../utils/constants";

const { selector } = ACTION_TYPES;
const {
  nextPaginationPage,
  previousPaginationPage,
  onTemplatedListItemSelected,
  selectMarker
} = ACTION_VALUES;
const { above } = FIELD_POSITION;
const { selectPharmacyError, manualPharmacy, submit } = FIELD_NAMES;
let contBtnErrorState = null;
let nextBtnErrorState = null;

class TemplatedListViewController extends BaseViewController {
  defaultPharmsNumberPerPage = 10;

  listItems = [];

  itemsByPage = [];

  builtItemsByTemplate = [];

  formState = {};

  getParams() {
    const selectedItem = this.appData?.selectedListItem;
    return selectedItem
      ? { ...selectedItem?.url_request_data, selectedData: selectedItem }
      : {};
  }

  prepareSubmit(values) {
    return {
      ...values,
      ...this.getParams()
    };
  }

  handleButtonClick(name, text, action, ...[, , eventStates]) {
    const { value, type } = action || {};

    if (eventStates) {
      this.updateStates(eventStates, true, [
        ...this.screen.fields,
        ...this.builtItemsByTemplate
      ]);
    }

    if (type === selector) {
      if (value === onTemplatedListItemSelected && text) {
        const { setSelectedListItem } = this.appData;
        setSelectedListItem(text);
      }
      if (value === selectMarker && text) {
        const { pickPharmacyOnMap } = this.appData;
        pickPharmacyOnMap(text);
      }
      if (value === nextPaginationPage || value === previousPaginationPage) {
        this.handlePaginationButtonClick(value);
      }
      return;
    }

    if (name === manualPharmacy) {
      this.formState = { ...manualPharmacyFormState };
    }

    if (action) {
      super.handleButtonClick(name, text, action);
    }
  }

  handlePaginationButtonClick(value) {
    let { pageNumber } = this.appData;
    const {
      pharmacies: { selected },
      screenRef,
      setPageNumber
    } = this.appData;

    if (value === previousPaginationPage && pageNumber > 1) {
      pageNumber--;
    } else if (
      value === nextPaginationPage &&
      pageNumber < this.itemsByPage.length
    ) {
      pageNumber++;
    }

    if (selected?.selectedOnMap) {
      selected.selectedOnMap = false;
    }

    setPageNumber({
      pageNumber
    });

    if (screenRef?.current?.scrollIntoView) {
      screenRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  getContainerFields = container =>
    container?.fieldLayouts?.[0]?.containerFields;

  getFieldByName = (fieldName, fields = this.screen.fields) =>
    fields.find(field => field?.name === fieldName);

  getPaginationContainerField() {
    const {
      data: {
        pagination: { container_name }
      }
    } = this.screen;
    return this.getFieldByName(container_name);
  }

  getPaginationButtonFields() {
    const containerFields = this.getContainerFields(
      this.getPaginationContainerField()
    );

    if (containerFields) {
      const {
        data: {
          pagination: { next_button_name, previous_button_name }
        }
      } = this.screen;
      const nextButtonField = this.getFieldByName(
        next_button_name,
        containerFields
      );
      const prevButtonField = this.getFieldByName(
        previous_button_name,
        containerFields
      );
      return { nextButtonField, prevButtonField };
    }
  }

  setFieldsVisibilityState = (field, isVisible) => {
    if (field?.layout) {
      field.layout.visible = isVisible;
    }
  };

  setPaginationButtonsVisibilityState() {
    const { pageNumber } = this.appData;
    const { prevButtonField, nextButtonField } =
      this.getPaginationButtonFields();

    this.setFieldsVisibilityState(prevButtonField, pageNumber > 1);
    this.setFieldsVisibilityState(
      nextButtonField,
      pageNumber < this.itemsByPage?.length
    );
  }

  paginateFields(listItems) {
    const pharmsPerPage =
      this.screen?.data?.pagination?.per_page ||
      this.defaultPharmsNumberPerPage;
    return listItems.reduce((acc, pharm, index) => {
      if (!(index % pharmsPerPage)) {
        acc = [...acc, []];
      }
      acc[acc.length - 1].push(pharm);
      return acc;
    }, []);
  }

  setPageNumberForPageTextField() {
    const {
      data: {
        pagination: { page_text_name }
      }
    } = this.screen;
    const containerFields = this.getContainerFields(
      this.getPaginationContainerField()
    );
    if (containerFields) {
      const pageNumberField = this.getFieldByName(
        page_text_name,
        containerFields
      );

      const pageTitle =
        this.appData?.settings?.translations?.page || "Page %page_number%";
      pageNumberField.title = pageTitle.replace(
        "%page_number%",
        this.appData.pageNumber
      );
    }
  }

  fixFieldsOrder(position = {}) {
    const fields = [...this.screen.fields];
    const { field_name = selectPharmacyError, alignment = above } = position;
    const index = this.screen.fields.findIndex(
      field => field?.name === field_name
    );
    const builtItemsIndex = alignment === above ? index : index + 1;

    if (this.itemsByPage?.length) {
      const { pageNumber } = this.appData;
      const currentItems =
        this.itemsByPage[pageNumber - 1] || this.itemsByPage[0];
      fields.splice(builtItemsIndex, 0, ...currentItems);
    }

    this.screen = {
      ...this.screen,
      fields
    };
  }

  getListItems() {
    const providerDetailData = this.appData?.providerDetailData?.default;
    const templatedList = this.screen?.data?.templated_list;
    return templatedList?.data?.default || providerDetailData;
  }

  saveFieldsDefaultState() {
    const continueBtn = this.getFieldByName(submit);
    const { nextButtonField } = this.getPaginationButtonFields();

    if (!contBtnErrorState && continueBtn) {
      contBtnErrorState = { ...continueBtn?.data };
    }

    if (!nextBtnErrorState && nextButtonField) {
      nextBtnErrorState = { ...nextButtonField?.data };
    }
  }

  setFieldsDefaultState() {
    const errorMessage = this.getFieldByName(selectPharmacyError);
    this.setFieldsVisibilityState(errorMessage, false);
    const continueBtn = this.getFieldByName(submit);

    if (continueBtn) {
      continueBtn.data = {
        ...contBtnErrorState,
        updatedActionEventStates: null
      };
      continueBtn.action = null;
    }
    const { nextButtonField, prevButtonField } =
      this.getPaginationButtonFields();

    if (nextButtonField) {
      nextButtonField.data = {
        ...nextBtnErrorState,
        updatedActionEventStates: null
      };
      prevButtonField.data = {
        ...nextBtnErrorState,
        updatedActionEventStates: null
      };
    }
  }

  preprocessScreenData() {
    const { data } = this.screen;
    const templatedList = data?.templated_list;
    const { fields, mapping } = templatedList;
    this.listItems = this.getListItems();

    if (this.listItems?.length) {
      this.builtItemsByTemplate = buildFieldByTemplate(
        fields?.default,
        mapping[0]?.rules,
        this.listItems,
        templatedList?.data?.default,
        this.listItems[0]
      );
    }

    this.itemsByPage = this.paginateFields(this.builtItemsByTemplate);
    this.fixFieldsOrder();

    if (data?.pagination) {
      const {
        data: {
          pagination: { per_page = this.defaultPharmsNumberPerPage }
        }
      } = this.screen;

      const paginationContainer = this.getPaginationContainerField();
      const showPaginationContainer = this.listItems?.length > per_page;

      this.setFieldsVisibilityState(
        paginationContainer,
        showPaginationContainer
      );

      if (showPaginationContainer) {
        this.setPageNumberForPageTextField();
        this.setPaginationButtonsVisibilityState();
      }
      this.saveFieldsDefaultState();

      if (!this.appData.screenInitialized) {
        this.setFieldsDefaultState();
        clearPreBuiltFields();
      }
    }
  }
}

export default TemplatedListViewController;
